/**
 * clinic-detail page — 側欄諮詢表單純前端驗證 + 送出 loading/成功/失敗 回饋
 *   + hero「預約諮詢」CTA 錨點平滑捲到表單並聚焦首欄
 *
 * 無後端：驗證通過後進送出中態（鈕 spinner + disabled 防重複送），以 setTimeout 模擬
 * 非同步；成功路徑開 #consult-success-modal、失敗路徑顯示表單頂 .alert--danger。
 * 接後端時把 simulateSubmit() 換成 fetch，成功 resolve / 失敗 reject 即沿用同一套回饋。
 * 重用既有 Modal primitive（components/modal.js，含 focus-trap / ESC / backdrop dismiss）。
 *
 * loading 態鏡像全站慣例（auth-forms.js / member-edit.js）：.is-submitting + 同一份
 * SPINNER_SVG（.member-spinner）；spin keyframe 因前台 main.css 鏈不含 member.css，
 * 由 clinic-detail.css 自備 clinic-spin，reduced-motion 退「送出中…」純文字不注入 spinner。
 *
 * 驗證規則（純前端，對齊 form-field 的 aria-invalid / --invalid 視覺）：
 *   - 姓名：必填
 *   - 電話：必填，台灣手機/市話寬鬆格式（數字、+、-、空白，至少 8 碼數字）
 *   - Email：選填，有填則需合法格式
 *   - 想了解療程：必選
 *   - 諮詢內容：必填
 *
 * Listener cleanup：保留 teardown()，HMR dispose 時解綁，避免重複綁定。
 */
import { Modal } from '../components/modal.js';
import { initFavToggle } from '../components/fav-toggle.js';
import { prefersReducedMotion } from '../utils/dom.js';
// 欄位 invalid 標記「三件套」→ 收斂至 field-error.js（單一真相源）。原內聯 setFieldError(fieldEl,msg) /
// clearFieldError(fieldEl) 改為薄包裝：自 fieldEl 取 control，委派 helper 的 setInvalid(control,msg) /
// clearInvalid(control)（簽名對外不變，callers 不動）。
// a11y/parity 註：consult 各欄由 _form-field.html 產（error:true → 已預置空 .form-field__error，但「無 id」、
//   控制項「無 aria-describedby」、hint「無 id」）。故 helper 的 describedby 切換因無 id 為 no-op、
//   **不新增 aria-describedby**（與原內聯一致）。唯一行為差異：email 欄帶 hint，helper 報錯時會把該 hint
//   收起（互斥顯示，避免兩行擠）、清錯時還原——原內聯不收 hint。屬刻意統一（對齊全站 form-field 慣例），非偷渡。
import { setInvalid, clearInvalid } from '../components/field-error.js';

const PHONE_RE = /^[0-9+\-\s]{8,}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// demo 模擬送出結果：false=成功（開 success modal）；改 true 可走失敗路徑（顯 error alert）。
// 接後端後刪此旗標，改以 fetch 的 resolve / reject 分流。
const SIMULATE_FAILURE = false;

// 與 auth-forms.js 同一份 spinner markup（共用 .member-spinner 樣式 + member-spin keyframe）。
const SPINNER_SVG =
  '<svg class="member-spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
  '<circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" ' +
  'stroke-dasharray="42" stroke-dashoffset="14" /></svg>';

function init() {
  const form = document.getElementById('consult-form');
  if (!form) return null;

  // 欄位定義：required 必填，validate 自訂規則（回 true = 合法）
  const fields = [
    { name: 'name', required: true },
    {
      name: 'phone',
      required: true,
      validate: (v) => PHONE_RE.test(v),
      invalidHint: '請輸入有效的聯絡電話',
    },
    {
      name: 'email',
      required: false,
      validate: (v) => v === '' || EMAIL_RE.test(v),
      invalidHint: '請輸入有效的 Email',
    },
    { name: 'treatment', required: true },
    { name: 'message', required: true },
  ];

  /** 設定單一欄位的 error 視覺與訊息（委派 field-error.js；簽名仍吃 .form-field 包裹，自取 control）。 */
  function setFieldError(fieldEl, message) {
    const input = fieldEl.querySelector('.form-field__input');
    if (input) setInvalid(input, message);
  }

  function clearFieldError(fieldEl) {
    const input = fieldEl.querySelector('.form-field__input');
    if (input) clearInvalid(input);
  }

  function validateField(cfg) {
    const fieldEl = form.querySelector(`[data-field="${cfg.name}"]`);
    if (!fieldEl) return true;
    const input = fieldEl.querySelector('.form-field__input');
    const value = (input?.value || '').trim();

    if (cfg.required && value === '') {
      setFieldError(fieldEl, '此欄位為必填');
      return false;
    }
    if (cfg.validate && !cfg.validate(value)) {
      setFieldError(fieldEl, cfg.invalidHint || '格式不符，請確認後重新輸入');
      return false;
    }
    clearFieldError(fieldEl);
    return true;
  }

  const submitBtn = form.querySelector('.consultation-form__submit');
  const errorAlert = form.closest('.consultation-form')?.querySelector('.consultation-form__alert');
  const submitLabel = submitBtn ? submitBtn.textContent.trim() : '送出諮詢';

  /* loading 進入 / 退出：鏡像 auth-forms.js（.is-submitting + spinner，reduced-motion 退純文字）。 */
  function enterSubmitting() {
    form.classList.add('is-submitting');
    if (!submitBtn) return;
    submitBtn.disabled = true;
    submitBtn.setAttribute('aria-disabled', 'true');
    submitBtn.innerHTML = prefersReducedMotion()
      ? '送出中…'
      : SPINNER_SVG + '<span>送出中…</span>';
  }
  function exitSubmitting() {
    form.classList.remove('is-submitting');
    if (!submitBtn) return;
    submitBtn.disabled = false;
    submitBtn.removeAttribute('aria-disabled');
    submitBtn.textContent = submitLabel;
  }

  function showError() {
    if (errorAlert) errorAlert.hidden = false;
  }
  function hideError() {
    if (errorAlert) errorAlert.hidden = true;
  }

  /* demo 模擬非同步送出（無後端）：接後端時換成 fetch(...)，成功 resolve / 失敗 reject。 */
  // TODO(backend): simulateSubmit() 換 fetch（resolve 開 modal / reject 顯 .alert--danger），刪 SIMULATE_FAILURE 旗標 — 見 docs/BACKEND_HANDOFF.md#clinic-consult-form
  function simulateSubmit() {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        SIMULATE_FAILURE ? reject(new Error('demo failure')) : resolve();
      }, 900);
    });
  }

  function onSubmit(e) {
    e.preventDefault();

    // 送出中防重複送（disabled 已擋點擊，這裡再保險擋 Enter 連送）
    if (form.classList.contains('is-submitting')) return;

    let firstInvalid = null;
    let allValid = true;

    for (const cfg of fields) {
      const ok = validateField(cfg);
      if (!ok && !firstInvalid) {
        firstInvalid = form.querySelector(`[data-field="${cfg.name}"] .form-field__input`);
      }
      allValid = allValid && ok;
    }

    if (!allValid) {
      firstInvalid?.focus();
      return;
    }

    // 驗證通過 → 清掉上次失敗提示，進送出中態，模擬非同步送出
    hideError();
    enterSubmitting();

    simulateSubmit()
      .then(() => {
        exitSubmitting();
        Modal.open('#consult-success-modal');
        form.reset();
      })
      .catch(() => {
        exitSubmitting();
        // 失敗提示為 role=alert（live region），顯示時 SR 自動朗讀，無需手動移焦
        showError();
      });
  }

  // input/change 即時清除該欄 error（改善體驗）
  function onFieldInput(e) {
    const fieldEl = e.target.closest('[data-field]');
    if (fieldEl && fieldEl.classList.contains('form-field--invalid')) {
      clearFieldError(fieldEl);
    }
  }

  form.addEventListener('submit', onSubmit);
  form.addEventListener('input', onFieldInput);
  form.addEventListener('change', onFieldInput);

  /* hero「預約諮詢」CTA → 平滑捲到表單並聚焦首欄。
     攔截錨點預設跳轉，改 scrollIntoView（reduced-motion 退 auto）；捲動後把焦點移到第一個
     輸入欄，讓鍵盤 / 螢幕閱讀器使用者直接落在表單起點（非僅視覺捲動）。 */
  const consultCta = document.querySelector('.clinic-hero-info__consult');
  function onConsultClick(e) {
    const formSection = form.closest('.consultation-form') || form;
    if (!formSection) return;
    e.preventDefault();
    formSection.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'start',
    });
    const firstInput = form.querySelector('.form-field__input');
    if (firstInput) {
      // 等捲動起步後再聚焦，避免聚焦瞬間把頁面瞬跳到欄位（蓋掉 smooth 動畫）
      window.setTimeout(() => firstInput.focus({ preventScroll: true }), 200);
    }
  }
  if (consultCta) consultCta.addEventListener('click', onConsultClick);

  /* 收藏診所 toggle — 共用 fav-toggle 模組（與 article-detail 同一套狀態機）。
     key medproof:fav:clinics（與 article 的 :articles 分桶）；rest=secondary、
     active=.button--added（深 teal，保留收藏為次級 CTA 不與主 CTA 同重，沿用 A2 決策）；
     愛心填滿 .is-filled、首次收藏彈跳 .is-popping。listener 入 favCleanups 於 teardown 解除。 */
  const favCleanups = [];
  initFavToggle(
    {
      selector: '[data-fav-toggle]',
      id: document.querySelector('[data-fav-toggle]')?.dataset.clinicId,
      storageKey: 'medproof:fav:clinics',
      restLabel: '收藏診所',
      activeLabel: '已收藏',
      iconSelector: '.clinic-hero-info__favorite-icon',
      restClass: 'button--secondary',
      activeClass: 'button--added',
    },
    favCleanups,
  );

  return function teardown() {
    form.removeEventListener('submit', onSubmit);
    form.removeEventListener('input', onFieldInput);
    form.removeEventListener('change', onFieldInput);
    if (consultCta) consultCta.removeEventListener('click', onConsultClick);
    favCleanups.forEach((fn) => fn());
  };
}

let teardown = null;
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    teardown = init();
  });
} else {
  teardown = init();
}

// Vite HMR：模組更新前解綁，避免 listener 重複疊加
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (typeof teardown === 'function') teardown();
  });
}
